import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { translateBatch } from "@/lib/translate";

/**
 * Translates the given string fields of a list of objects into the active
 * language (French) when it differs from English. Returns the original list
 * untouched for English visitors, and a translated copy for French visitors.
 *
 * The dependency is a content *signature* (not the array reference), so it is
 * safe to pass freshly-derived arrays (e.g. `.filter()` / `.slice()` results)
 * without triggering infinite re-renders.
 *
 * Usage:
 *   const programs = useTranslatedList(rawPrograms, ["name", "description", "category"]);
 */
export function useTranslatedList<T>(items: T[], fields: string[]): T[] {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || "en";

  const fieldKey = fields.join("|");
  const signature = `${lang}|${fieldKey}|${JSON.stringify(
    (items ?? []).map((it) =>
      fieldKey.split("|").map((f) => ((it as Record<string, unknown>)[f] ?? "") as string),
    ),
  )}`;

  const [result, setResult] = useState<T[]>(items ?? []);

  useEffect(() => {
    let active = true;

    if (lang === "en" || !items || items.length === 0) {
      setResult(items ?? []);
      return () => {
        active = false;
      };
    }

    const texts: string[] = [];
    const refs: Array<[number, string]> = [];

    items.forEach((item, i) => {
      const record = item as Record<string, unknown>;
      fields.forEach((f) => {
        const v = record[f];
        if (typeof v === "string" && v.trim()) {
          texts.push(v);
          refs.push([i, f]);
        }
      });
    });

    if (texts.length === 0) {
      setResult(items);
      return () => {
        active = false;
      };
    }

    // Reset to the raw list immediately so we never show stale translations
    // while a new batch is in flight.
    setResult(items);

    translateBatch(texts, lang).then((translated) => {
      if (!active) return;
      const next = items.map((it) => ({ ...it })) as T[];
      refs.forEach(([i, f], idx) => {
        const val = translated[idx];
        if (val && val !== texts[idx]) {
          (next[i] as Record<string, unknown>)[f] = val;
        }
      });
      setResult(next);
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return result;
}

/**
 * Translates the given string fields of a single object into the active
 * language. Convenience wrapper around useTranslatedList for settings records,
 * taglines, etc. that are one object rather than a list.
 */
export function useTranslatedRecord<T extends Record<string, unknown>>(
  record: T,
  fields: string[],
): T {
  const list = useMemo(() => [record], [record]);
  return useTranslatedList(list, fields)[0];
}